import { z } from "zod";
import { isoDateSchema } from "@common/helpers/date";
import { InvoiceStatus } from "@common/types/invoice";

export const updateInvoiceBodySchema = z
    .object({
        memo: z.string().max(1000).nullable().optional(),
        status: z.nativeEnum(InvoiceStatus).optional(),
        uncollectableAt: isoDateSchema.nullable().optional(),
        voidedAt: isoDateSchema.nullable().optional(),
        paidAt: z.coerce.date().nullable().optional(),
        due: z.coerce.date().nullable().optional(),
        // Authorizes voiding an Open invoice that has transaction matches, as
        // part of a revision flow. Identifies the revision Draft that must link
        // back to this invoice; the backend verifies the linkage before it
        // skips the "matches block Void" rule. Only valid alongside a
        // { status: VOID, voidedAt } body — the ordinary status dropdown never
        // sends it, so its behavior is unchanged. See §5.3.
        //
        // Bounding beyond the UUID shape (self-reference guard + full lineage
        // validation) lives in InvoiceService#isVerifiedRevision, which runs
        // inside the update transaction: this is a body-only schema and cannot
        // reference the path `invoiceId` it must differ from.
        revisionInvoiceId: z.string().uuid().optional(),
    })
    .refine(
        (data) =>
            data.revisionInvoiceId === undefined ||
            (data.status === InvoiceStatus.VOID &&
                data.voidedAt !== undefined &&
                data.voidedAt !== null),
        {
            message:
                "revisionInvoiceId is only valid together with status void and a voidedAt date",
        },
    )
    .refine(
        (data) =>
            data.memo !== undefined ||
            data.status !== undefined ||
            data.uncollectableAt !== undefined ||
            data.voidedAt !== undefined ||
            data.paidAt !== undefined ||
            data.due !== undefined,
        {
            message:
                "At least one of memo, status, uncollectableAt, voidedAt, paidAt, or due must be provided",
        },
    )
    .refine(
        (data) =>
            data.status !== InvoiceStatus.UNCOLLECTABLE ||
            (data.uncollectableAt !== undefined &&
                data.uncollectableAt !== null),
        {
            message:
                "uncollectableAt is required when setting status to uncollectable",
        },
    )
    .refine(
        (data) => {
            if (data.uncollectableAt === null) {
                return (
                    data.status !== undefined &&
                    data.status !== InvoiceStatus.UNCOLLECTABLE
                );
            }
            return true;
        },
        {
            message:
                "Cannot clear uncollectableAt without changing status away from uncollectable",
        },
    )
    .refine(
        (data) => {
            if (
                data.uncollectableAt !== undefined &&
                data.uncollectableAt !== null
            ) {
                return data.status === InvoiceStatus.UNCOLLECTABLE;
            }
            return true;
        },
        {
            message:
                "uncollectableAt can only be set when status is uncollectable",
        },
    )
    .refine(
        (data) =>
            data.status !== InvoiceStatus.VOID ||
            (data.voidedAt !== undefined && data.voidedAt !== null),
        {
            message: "voidedAt is required when setting status to void",
        },
    )
    .refine(
        (data) => {
            if (data.voidedAt === null) {
                return (
                    data.status !== undefined &&
                    data.status !== InvoiceStatus.VOID
                );
            }
            return true;
        },
        {
            message:
                "Cannot clear voidedAt without changing status away from void",
        },
    )
    .refine(
        (data) => {
            if (data.voidedAt !== undefined && data.voidedAt !== null) {
                return data.status === InvoiceStatus.VOID;
            }
            return true;
        },
        {
            message: "voidedAt can only be set when status is void",
        },
    )
    .refine(
        (data) =>
            data.status !== InvoiceStatus.PAID ||
            (data.paidAt !== undefined && data.paidAt !== null),
        {
            message: "paidAt is required when setting status to paid",
        },
    )
    .refine(
        (data) => {
            if (data.paidAt === null) {
                return (
                    data.status !== undefined &&
                    data.status !== InvoiceStatus.PAID
                );
            }
            return true;
        },
        {
            message:
                "Cannot clear paidAt without changing status away from paid",
        },
    )
    .refine(
        (data) => {
            if (data.paidAt !== undefined && data.paidAt !== null) {
                return data.status === InvoiceStatus.PAID;
            }
            return true;
        },
        {
            message: "paidAt can only be set when status is paid",
        },
    );
